/**
 * Vendor module — service layer.
 *
 * CRUD for vendor master data. Bridges the UI to the repository. Vendor
 * numbers are server-allocated (VND kind) via the shared sequence allocator.
 * The localStorage mirror was deleted in the dual-path retirement (Phase 2b)
 * — local dev runs against the emulator (`npm run dev:emu`).
 */

import { repositories } from '../../../data/repositories';
import { allocateSequenceNumber } from '../../../lib/sequenceAllocator';
import type { Vendor, EntityId } from '../../../types';

/** The editable fields a caller supplies — id, vendorNumber and the audit
 *  stamps are filled in by the service. */
export type VendorInput = Omit<
  Vendor,
  'id' | 'vendorNumber' | 'createdAt' | 'createdBy' | 'updatedAt' | 'updatedBy'
>;

const persistVendor = async (vendor: Vendor): Promise<void> => {
  await repositories.vendors.save(vendor);
};

export const listVendors = async (): Promise<Vendor[]> => {
  const vendors = await repositories.vendors.list();
  return vendors.sort((a, b) => a.displayName.localeCompare(b.displayName));
};

export const fetchVendor = async (id: EntityId): Promise<Vendor | null> => {
  return repositories.vendors.findById(id);
};

export const createVendor = async (input: VendorInput, actor: string): Promise<Vendor> => {
  const { number: vendorNumber } = await allocateSequenceNumber('VND');
  const now = new Date().toISOString();
  const vendor: Vendor = {
    ...input,
    id: `vnd_${crypto.randomUUID()}`,
    vendorNumber,
    createdAt: now,
    createdBy: actor,
    updatedAt: now,
    updatedBy: actor,
  };
  await persistVendor(vendor);
  return vendor;
};

export const updateVendor = async (
  id: EntityId,
  input: VendorInput,
  actor: string,
): Promise<Vendor> => {
  const current = await fetchVendor(id);
  if (!current) throw new Error(`Vendor ${id} not found.`);
  const updated: Vendor = {
    ...current,
    ...input,
    updatedAt: new Date().toISOString(),
    updatedBy: actor,
  };
  await persistVendor(updated);
  return updated;
};
